import React from 'react';
import { AttachmentCard } from '@/components/ui/AttachmentCard/AttachmentCard';
import { buildMessengerAttachmentCardTopHeader } from '@/components/Messenger/shared/attachments/MessengerAttachmentCardTopHeader';
import type { TeleportContextValue } from '@/hooks/useTeleportToTarget';
import { ProjectChatCard } from '@/features/newdesign/projects/chat/ProjectChatCard';

export type MessengerContextAttachment =
  | { type: 'project'; id: string; name?: string }
  | { type: 'session'; id: string; date: string; label?: string }
  | { type: 'planner' };

export function ContextAttachmentChips({
  attachments,
  teleport,
  header,
  peerAuthorName,
  forwardedFromSenderName,
  replyQuote,
  isOwn = false,
}: {
  attachments: readonly MessengerContextAttachment[];
  teleport: TeleportContextValue | null;
  /** Имя в текстовой колонке, если нет шапки карточки. */
  header?: string | null;
  peerAuthorName?: string | null;
  forwardedFromSenderName?: string | null;
  replyQuote?: React.ReactNode;
  isOwn?: boolean;
}): React.ReactNode {
  const topHeader = buildMessengerAttachmentCardTopHeader({
    peerAuthorName,
    forwardedFromSenderName,
    replyQuote,
    isOwn,
  });
  const useTopHeader = Boolean(topHeader);

  const chips: React.ReactNode[] = [];
  for (let i = 0; i < attachments.length; i++) {
    const a = attachments[i];
    const cardTopHeader = i === 0 && useTopHeader ? topHeader : undefined;
    const cardHeader = cardTopHeader ? null : header;

    if (a.type === 'project' && a.id) {
      // Карточка нового дизайна — как строка списка проектов; автор, пересылка и цитата — строкой над ней
      const projectTopHeader =
        i === 0
          ? buildMessengerAttachmentCardTopHeader({
              peerAuthorName: peerAuthorName ?? header,
              forwardedFromSenderName,
              replyQuote,
              isOwn,
            })
          : null;
      chips.push(
        <div key={`p-${a.id}-${i}`}>
          {projectTopHeader}
          <ProjectChatCard projectId={a.id} name={a.name || a.id} onOpen={(id) => teleport?.goToProject(id)} />
        </div>
      );
      continue;
    }
    if (a.type === 'session' && a.id && a.date) {
      chips.push(
        <AttachmentCard
          key={`s-${a.id}-${i}`}
          variant="session"
          topHeader={cardTopHeader}
          header={cardHeader}
          title={a.label || a.date}
          subtitle="Сессия"
          onClick={() => teleport?.goToSession({ id: a.id, date: a.date! })}
        />
      );
      continue;
    }
    if (a.type === 'planner') {
      chips.push(
        <AttachmentCard
          key={`planner-${i}`}
          variant="planner"
          topHeader={cardTopHeader}
          header={cardHeader}
          title="Планер"
          subtitle="Контекст"
        />
      );
    }
  }
  if (chips.length === 0) return null;
  return <div className="chatAssistant__sentAttachRow">{chips}</div>;
}
